
import { FC, useCallback, useContext, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";

import { validate } from "capitalrx-components-2.0";
import { Form, SubmitButton, useForm, Textbox } from "capitalrx-components-2.0/lib/forms";
import { Button, Link, Message, Messages, useAlert } from "capitalrx-components-2.0/lib/general";
import { Stack, Shelf, useResponsive } from "capitalrx-components-2.0/lib/layout";
import { Body, Semibold, Subtitle } from "capitalrx-components-2.0/lib/typography";

import { authLoginInit } from "@/api";
import { BackLink } from "@/components";
import { AuthenticationContext, ThemeSelectorContext } from "@/context";
import { ANALYTIC_EVENTS, logEvent } from "@/service/analyticsService";
import { getThemeButton, getThemeURL } from "@/utils";
import { getApiBaseUrl } from "@/api/getApiBaseUrl";

import styles from "./Login.module.scss";


export interface LoginForm {
  readonly email: string;
  readonly emailed_otp: string;
}

type formStep = "init" | "otp";


export const Login: FC = () => {
  const navigate = useNavigate();
  const showAlert = useAlert();
  const [searchParams] = useSearchParams();

  const { primary_color } = useContext(ThemeSelectorContext);

  const form = useForm<LoginForm>();
  const [formStep, setFormStep] = useState<formStep>("init");
  const [sessionToken, setSessionToken] = useState<string>();
  const { isUserLoggedIn, loginOtp, loginClear, getUser } = useContext(AuthenticationContext);
  const [email, setEmail] = useState<string>();
  const [loginError, setLoginError] = useState<string>();
  const [isClearAuthProcessing, setIsClearAuthProcessing] = useState<boolean>(false);

  const getPostLoginRedirect = (): string | null => {
    const redirectParam = searchParams.get("redirect");
    if (redirectParam && redirectParam.startsWith("/") && !redirectParam.startsWith("//")) {
      return redirectParam;
    }

    return null;
  };

  // Check for Clear authentication features
  const clearEnabled = searchParams.has("clear_enabled");
  const clearState = searchParams.get("state");

  const { isCompressed } = useResponsive({
    isCompressed: {
      desktop: false,
      tablet: false,
      mobile: true,
    },
  });
  const StackOrShelf = isCompressed ? Stack : Shelf;

  useEffect(() => {
    if (isUserLoggedIn()) {
      navigate(getPostLoginRedirect() || getThemeURL("HOME"));
    }
  },
    [isUserLoggedIn, navigate],
  );

  useEffect(() => {
    const handleClearRedirect = async () => {
      if (clearState && !isUserLoggedIn()) {
        setIsClearAuthProcessing(true);
        try {
          setLoginError(null);
          logEvent(ANALYTIC_EVENTS.AUTH.loginClear);

          await loginClear(clearState);

          // Fetch and cache the logged in user in session
          await getUser();

          navigate(getPostLoginRedirect() || getThemeURL("HOME"));
        } catch (error) {
          console.error("Clear authentication failed:", error);
          setLoginError("Clear authentication failed. Please try again.");
          logEvent(ANALYTIC_EVENTS.ERRORS.loginFailure);

          // Remove state param to show normal login
          navigate("/Login", { replace: true });
        } finally {
          setIsClearAuthProcessing(false);
        }
      }
    };

    handleClearRedirect();
  }, [clearState, isUserLoggedIn, loginClear, getUser, navigate]);

  const handleLoginInit = useCallback(
    async (data: LoginForm) => {
      setEmail(null);
      setLoginError(null);
      logEvent(ANALYTIC_EVENTS.AUTH.loginInit);
      await sendCode(data.email);
    },
    [],
  );

  const sendCode = useCallback(
    async (email: string, alert: boolean = false) => {
      try {
        const loginInitResponse = await authLoginInit({ email });
        setSessionToken(loginInitResponse["session"]);
        setEmail(email);
        setFormStep("otp");

        if (alert) {
          showAlert({
            type: "success",
            style: "banner",
            message: "Security code sent",
          });
        }

      } catch (error) {
        console.error(error);
        setLoginError("Failed to log in");
        logEvent(ANALYTIC_EVENTS.ERRORS.loginFailure);
      }
    },
    [],
  );

  const handleLoginOtp = useCallback(
    async (data: LoginForm) => {
      setLoginError(null);
      logEvent(ANALYTIC_EVENTS.AUTH.loginOTP);

      try {
        const result = await loginOtp(
          data.email,
          data.emailed_otp,
          sessionToken,
        );

        // fetch and cache the logged in user in session
        await getUser();

        if (result === "new_user") {
          // If this is a new user, direct them to their new account page
          navigate(getThemeURL("ACCOUNT"));
        } else {
          navigate(getPostLoginRedirect() || getThemeURL("HOME"));
        }

      } catch (error) {
        setLoginError("Incorrect security code");
        logEvent(ANALYTIC_EVENTS.ERRORS.loginFailure);
      }

    },
    [navigate, sessionToken],
  );

  const handleClearLogin = useCallback(() => {
    const apiBaseUrl = getApiBaseUrl();
    window.location.href = `${apiBaseUrl}/clear_auth/login`;
  }, []);

  return (
    <Stack className={styles.LoginContainer} spacing="medium">
      <BackLink linkText="Home" />

      {isClearAuthProcessing && (
        <Stack spacing="large">
          <Subtitle>Completing Clear authentication...</Subtitle>
          <Body>Please wait while we verify your identity.</Body>
        </Stack>
      )}

      {!isClearAuthProcessing && formStep === "init" && !clearEnabled
        && <Stack spacing="large">
          <Shelf>
            <Subtitle>Log In or Create Account</Subtitle>
            <Shelf.Spacer />
          </Shelf>

          <Form form={form} onSubmit={handleLoginInit}>
            <Stack>
              <Textbox.Field
                required
                label="Email address"
                name={"email"}
                placeholder="email@yourcompany.com"
                validator={(value) => validate.isEmail(value)}
                instructions="Provide a professional email from your practice (e.g. dr.smith@healthclinic.com). Personal emails are accepted, but we strongly recommend using a practice-based address."
                testId="test__login_email"
              />
              {loginError
                && <Message appearance={Messages.Error} visible>
                  {loginError}
                </Message>
              }
              <StackOrShelf>
                <SubmitButton appearance={getThemeButton("primary", primary_color)} testId="test__continue_button">Continue</SubmitButton>
                <Shelf.Spacer />
              </StackOrShelf>
            </Stack>
          </Form>
        </Stack>
      }

      {!isClearAuthProcessing && formStep === "init" && clearEnabled
        && <Stack spacing="large">
          <Shelf>
            <Subtitle>Log In or Create Account</Subtitle>
            <Shelf.Spacer />
          </Shelf>

          <Stack spacing="medium">
            <Body>Use your Clear account to quickly and securely verify your identity.</Body>
            {loginError
              && <Message appearance={Messages.Error} visible>
                {loginError}
              </Message>
            }
            <StackOrShelf>
              <Button
                appearance={getThemeButton("primary", primary_color)}
                onClick={handleClearLogin}
                testId="test__clear_login_button"
              >
                Log In with Clear
              </Button>
              <Shelf.Spacer />
            </StackOrShelf>
          </Stack>
        </Stack>
      }

      {!isClearAuthProcessing && formStep === "otp"
        && <Stack spacing="large">
          <Stack>
            <Subtitle>Enter Code to Log In</Subtitle>
            <Stack spacing="none">
              <Body>We sent a security code to <Semibold>{email}</Semibold>.</Body>
              <Shelf spacing="mini">
                <Body>The code expires in 15 minutes</Body>
                <Link onClick={() => sendCode(email, true)} to={null}> Resend code</Link>
                <Shelf.Spacer />
              </Shelf>
            </Stack>
          </Stack>

          <Form form={form} onSubmit={handleLoginOtp}>
            <Stack>
              <Textbox.Field required label="Security Code" name={"emailed_otp"} testId="test__security_key" />
              {loginError
                && <Message appearance={Messages.Error} visible>
                  {loginError}
                </Message>
              }
              <StackOrShelf>
                <SubmitButton appearance={getThemeButton("primary", primary_color)} testId="test__continue_button">Continue</SubmitButton>
                <Shelf.Spacer />
                <Button
                  appearance={getThemeButton("secondary", primary_color)}
                  onClick={() => setFormStep("init")}
                >
                  Back
                </Button>
              </StackOrShelf>
            </Stack>
          </Form>
        </Stack>
      }
    </Stack >
  );
};

export default Login;
